import { useTranslation } from 'react-i18next';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { useEffect, useState } from 'react';
import LineChart from '../LineChart';
import { apiGet } from '../../services/_common';
import SelectBox from '../SelectBox';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import IssueGuide from '../IssueGuide';
import Skeleton from 'react-loading-skeleton';
import { useRecoilValue } from 'recoil';
import { userState } from '../../atoms';
import { useQuery } from '@tanstack/react-query';

// 측정기 차트
export const PmInfo = ({ name, sensorCd: sensor }: any) => {
  const userInfo = useRecoilValue(userState);
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const toDay = todayYYYYMMDD();
  const [fNameList, setFnameList] = useState<any[] | null>(null);
  const [device, setDevice] = useState({ cdName: '', subCd: '' });
  const [datasets, setDatasets] = useState<any>({ labels: [], datasets: [] });
  const [defaultSeneor, setDefaultSeneor] = useState('');
  // const [sensor, setSensor] = useState<string>(sensorCd);

  // const {
  //   data: sensorData,
  //   isLoading,
  //   isError,
  //   refetch,
  // } = GetFetchStatus({
  //   path: '/sensor/detail',
  //   req: { grCd: sensor },
  // });

  const {
    data: sensorData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['sensorDetail', hCd, sCd, sensor],
    queryFn: () => getSensorDetailAPI(),
    enabled: !!hCd && !!sCd,
  });

  const getSensorDetailAPI = async () => {
    const req = { grCd: sensor, hCd, sCd };
    const res = await apiGet({ path: '/sensor/detail', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      return data;
    }
    throw new Error('error');
  };

  useEffect(() => {
    if (sensorData) {
      const { sensorList } = sensorData;

      const newArray = sensorList.map((v: any) => {
        return {
          sDefault: v.sDefault,
          type: 'subCd',
          subCd: v.subCd,
          cdName: v.sName,
        };
      });
      setFnameList(newArray);
      const defaultSensorObj = newArray.find((el: any) => el.sDefault === 'Y');
      if (defaultSensorObj) {
        setDefaultSeneor(defaultSensorObj.cdName);
        setDevice({ subCd: defaultSensorObj.subCd, cdName: defaultSensorObj.cdName });
      }
    }
  }, [sensorData]);

  // 미세먼지 센서 일별 조회 API
  const getPmSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/pmDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });

      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'PM2.5',
            data: sortedData.map((item: any) => Number(item.pm25)),
            borderColor: 'rgba(0, 119, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(0, 119, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(0, 119, 255, 0)');
              return gradient;
            },
            fill: true,
          },
          {
            label: 'PM10',
            data: sortedData.map((item: any) => Number(item.pm10)),
            borderColor: 'rgba(0, 221, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(0, 221, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(0, 221, 255, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 소음 센서 일별 조회 API
  const getSoundSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/soundDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'dB',
            data: sortedData.map((item: any) => Number(item.sound)),
            borderColor: 'rgba(0, 119, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(0, 119, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(0, 119, 255, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 질소 센서 일별 조회 API
  const getNoxSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/noxDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'PPM',
            data: sortedData.map((item: any) => Number(item.nox)),
            borderColor: 'rgba(0, 0, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(0, 0, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(0, 0, 255, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 풍속 센서 일별 조회 API
  const getWindSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/windDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'm/s',
            data: sortedData.map((item: any) => Number(item.wind)),
            borderColor: 'rgba(150, 0, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(150, 0, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(150, 0, 255, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 산소 일별 조회 API
  const getO2SensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/o2Day', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: '% vol',
            data: sortedData.map((item: any) => Number(item.o2)),
            borderColor: 'rgba(255, 0, 255, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(255, 0, 255, 0.2)');
              gradient.addColorStop(1, 'rgba(255, 0, 255, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 가스 센서 일별 조회 API
  const getMqSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/mqDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'PPM',
            data: sortedData.map((item: any) => Number(item.mq)),
            borderColor: 'rgba(255, 0, 110, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(255, 0, 110, 0.2)');
              gradient.addColorStop(1, 'rgba(255, 0, 110, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 진동 센서 일별 조회 API
  const getVibroSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/vibroDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'dBV',
            data: sortedData.map((item: any) => Number(item.vibro)),
            borderColor: 'rgba(255, 0, 110, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(255, 0, 110, 0.2)');
              gradient.addColorStop(1, 'rgba(255, 0, 110, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 기울기(경사계) 센서 일별 조회 API
  const getTiltSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/tiltDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: '%',
            data: sortedData.map((item: any) => Number(item.tilt)),
            borderColor: 'rgba(255, 165, 0, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(255, 165, 0, 0.2)');
              gradient.addColorStop(1, 'rgba(255, 165, 0, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 균열 센서 일별 조회 API
  const getCrackSensorDayAPI = async () => {
    const req = { hCd, sCd, dvNo: device.subCd, date: toDay, time: '1' };
    const res = await apiGet({ path: '/sensor/crackDay', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const sortedData = data.sort((a: any, b: any) => {
        return a.checkTime.localeCompare(b.checkTime);
      });
      const newDataset = {
        labels: sortedData.map((el: any) => el.checkTime.slice(-3)),
        datasets: [
          {
            label: 'mm',
            data: sortedData.map((item: any) => Number(item.crack)),
            borderColor: 'rgba(128, 128, 128, 0.5)',
            backgroundColor: (context: any) => {
              const { ctx } = context.chart;
              const gradient = ctx.createLinearGradient(0, 0, 0, 200);
              gradient.addColorStop(0, 'rgba(128, 128, 128, 0.2)');
              gradient.addColorStop(1, 'rgba(128, 128, 128, 0)');
              return gradient;
            },
            fill: true,
          },
        ],
      };
      setDatasets(newDataset);
    } else {
      // toast.error(t(ERROR));
    }
  };

  useEffect(() => {
    if (device.subCd) {
      if (sensor === '01') getPmSensorDayAPI();
      if (sensor === '02') getSoundSensorDayAPI();
      if (sensor === '03') getNoxSensorDayAPI();
      if (sensor === '04') getWindSensorDayAPI();
      if (sensor === '05') getO2SensorDayAPI();
      if (sensor === '06') getMqSensorDayAPI();
      if (sensor === '10') getVibroSensorDayAPI();
      if (sensor === '11') getTiltSensorDayAPI();
      if (sensor === '12') getCrackSensorDayAPI();
    }
  }, [device]);

  const onClickRefresh = () => {
    refetch();
    if (sensor === '01') getPmSensorDayAPI();
    if (sensor === '02') getSoundSensorDayAPI();
    if (sensor === '03') getNoxSensorDayAPI();
    if (sensor === '04') getWindSensorDayAPI();
    if (sensor === '05') getO2SensorDayAPI();
    if (sensor === '06') getMqSensorDayAPI();
    if (sensor === '10') getVibroSensorDayAPI();
    if (sensor === '11') getTiltSensorDayAPI();
    if (sensor === '12') getCrackSensorDayAPI();
  };

  const loading = !sensorData || isLoading;

  if (isError) return <IssueGuide text='센서 정보를 불러오는데 실패했습니다.' type='error' />;

  return (
    <div className='pmInfo'>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
          <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
            <span className='material-symbols-rounded'>refresh</span>
          </div>
        </div>
      </div>
      <div className='widget-body graph'>
        {!fNameList || loading ? <Skeleton width={370} height={40} /> : <SelectBox options={fNameList} defaultOption={defaultSeneor} state={device} setState={setDevice} stateKey='subCd' />}
        <div className='lineChartWrapper'>
          <LineChart labels={datasets.labels} datasets={datasets.datasets} title='' showLegend />
        </div>
      </div>
    </div>
  );
};
